import type {
	DraftVerificationAttachment,
	VerificationAttachment,
} from '@api-client/api'
import { ANSWERED_UNIT_ADORNMENT } from '@components/controllers/jsonforms/AnsweredUnitContext'
import {
	evalRule,
	getAtPath,
	scopeToPath,
} from '@components/controllers/jsonforms/cleanHiddenFields'
import {
	I18nContext,
	type TranslateFn,
} from '@components/controllers/jsonforms/I18nContext'
import { InfoKeyValue } from '@components/InfoKeyValue'
import type { FormDefinition } from '@components/JsonFormsRenderer/JsonFormsRenderer'
import {
	DetailsContainer,
	DetailsSpacer,
	DetailsSubContainer,
	DetailsTitle,
} from '@components/ProductRequestDetails/styles'
import type { JsonSchema, UISchemaElement } from '@jsonforms/core'
import { Box, Typography } from '@mui/material'
import { Fragment, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// Matches the placeholder used by NumberRenderer for emission value fields.
const PLACEHOLDER_ADORNMENT = 'kgCO2eq / unit'

// Normalise unit strings: "kgCO2eq / kg" → "kgCO₂e/kg"
const formatUnit = (unit: string): string =>
	unit.replaceAll('CO2eq', 'CO\u2082e').replaceAll(' / ', '/')

interface FormDataViewerProps {
	formDefinition: FormDefinition
	formData: Record<string, unknown>
	onDownload?: (attachmentId: string) => void
}

/**
 * Navigate rootSchema following a JSONForms scope string.
 * e.g. "#/properties/emissionValues/properties/referenceYear" → the referenceYear sub-schema.
 */
const resolveSchemaByScope = (
	rootSchema: JsonSchema,
	scope: string
): JsonSchema => {
	const parts = scope.replace(/^#\//, '').split('/')
	let current: Record<string, unknown> = rootSchema as Record<string, unknown>
	for (const part of parts) {
		if (!current || typeof current !== 'object') {
			return {}
		}
		current = (current as Record<string, unknown>)[part] as Record<
			string,
			unknown
		>
	}
	return (current ?? {}) as JsonSchema
}

/**
 * Format a raw formData value for display.
 * Exported for unit testing.
 */
export const formatFieldValue = (
	value: unknown,
	fieldSchema: JsonSchema,
	options: Record<string, unknown> | undefined,
	path: string,
	translate: TranslateFn,
	t: (key: string) => string,
	formData: Record<string, unknown>
):
	| string
	| readonly (VerificationAttachment | DraftVerificationAttachment)[] => {
	if (value === undefined || value === null) {
		return '-'
	}

	// Attachment (options.format === 'attachment')
	if (options?.format === 'attachment') {
		const attachments = value as (
			| VerificationAttachment
			| DraftVerificationAttachment
		)[]
		return attachments.length === 0 ? '-' : attachments
	}

	// Boolean (includes checkbox format)
	if (typeof value === 'boolean') {
		return value ? t('yes') : t('no')
	}

	// Date
	const schemaFormat = fieldSchema.format
	if (typeof value === 'string' && schemaFormat === 'date') {
		return new Date(value).toLocaleDateString()
	}

	// Enum / oneOf (includes radio and multi-select)
	const itemSchema =
		fieldSchema.type === 'array'
			? (fieldSchema.items as JsonSchema | undefined)
			: fieldSchema
	if (itemSchema?.oneOf || itemSchema?.enum) {
		const propertyName = path.split('.').at(-1) ?? path
		// Resolve title from oneOf items as fallback (matches SelectRenderer behaviour)
		const getTitleForValue = (v: string): string => {
			const oneOfItems = itemSchema?.oneOf as
				| { const?: unknown; title?: unknown }[]
				| undefined
			const item = oneOfItems?.find(i => String(i.const) === v)
			return (item?.title as string | undefined) ?? v
		}
		if (Array.isArray(value)) {
			return (value as string[])
				.map(
					v =>
						translate(`${propertyName}.options.${v}`, undefined) ||
						formatUnit(getTitleForValue(v))
				)
				.join(', ')
		}
		const strValue = String(value)
		return (
			translate(`${propertyName}.options.${strValue}`, undefined) ||
			formatUnit(getTitleForValue(strValue))
		)
	}

	// Number
	if (typeof value === 'number') {
		const endAdornment = options?.endAdornment as string | undefined
		if (endAdornment === '%') {
			return `${value}%`
		}
		if (endAdornment === PLACEHOLDER_ADORNMENT) {
			const answeredUnit = formData.answeredUnit as string | undefined
			const resolved = answeredUnit
				? ANSWERED_UNIT_ADORNMENT[
						answeredUnit as keyof typeof ANSWERED_UNIT_ADORNMENT
					]
				: undefined
			return `${value} ${formatUnit(resolved ?? endAdornment)}`
		}
		if (endAdornment) {
			return `${value} ${formatUnit(endAdornment)}`
		}
		return String(value)
	}

	return String(value)
}

/** Convert a camelCase or snake_case key to Title Case for display. */
const formatCustomFieldLabel = (key: string): string =>
	key
		.replaceAll('_', ' ')
		.replaceAll(/([A-Z])/g, ' $1')
		.replace(/^./, s => s.toUpperCase())
		.trim()

/** Format a scalar custom field value as a display string. */
const formatCustomFieldValue = (value: unknown): string => {
	if (value === null || value === undefined) {
		return '-'
	}
	if (typeof value === 'boolean') {
		// Fallback only — callers that have access to t() should translate booleans themselves.
		return value ? 'Yes' : 'No'
	}
	if (typeof value === 'number') {
		return String(value)
	}
	if (typeof value === 'string') {
		return value || '-'
	}
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return '-'
		}
		return value
			.map(item =>
				typeof item === 'object' && item !== null
					? JSON.stringify(item)
					: String(item ?? '-')
			)
			.join(', ')
	}
	return JSON.stringify(value)
}

/**
 * Renders a custom field entry. Nested objects are rendered recursively as indented sub-rows
 * rather than collapsed into a single string.
 */
const CustomFieldEntry = ({
	fieldKey,
	value,
	depth = 0,
}: {
	fieldKey: string
	value: unknown
	depth?: number
}) => {
	const { t } = useTranslation()
	const label = formatCustomFieldLabel(fieldKey)
	if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
		const entries = Object.entries(value as Record<string, unknown>)
		return (
			<>
				<InfoKeyValue className="key-value-container" label={label} value="" />
				<Box sx={{ pl: (depth + 1) * 2 }}>
					{entries.map(([k, v]) => (
						<CustomFieldEntry
							key={k}
							fieldKey={k}
							value={v}
							depth={depth + 1}
						/>
					))}
				</Box>
			</>
		)
	}
	const displayValue =
		typeof value === 'boolean'
			? value
				? t('yes')
				: t('no')
			: formatCustomFieldValue(value)
	return (
		<InfoKeyValue
			className="key-value-container"
			label={label}
			value={displayValue}
		/>
	)
}

/**
 * Collect all Control scope strings referenced in a UISchema element tree.
 * Used to detect custom fields that exist in formData but have no UISchema control.
 */
const collectScopesFromElements = (
	elements: UISchemaElement[]
): Set<string> => {
	const scopes = new Set<string>()
	const visit = (el: UISchemaElement): void => {
		const elAny = el as unknown as Record<string, unknown>
		if (
			el.type === 'Control' ||
			el.type === 'FieldArrayBlock' ||
			el.type === 'EmissionsBreakdown'
		) {
			const scope = elAny.scope as string | undefined
			if (scope) {
				scopes.add(scope)
			}
		}
		const children = (elAny.elements as UISchemaElement[] | undefined) ?? []
		for (const child of children) {
			visit(child)
		}
	}
	for (const element of elements) {
		visit(element)
	}
	return scopes
}

const renderElement = (
	el: UISchemaElement,
	data: Record<string, unknown>,
	rootSchema: JsonSchema,
	translate: TranslateFn,
	t: (key: string) => string,
	onDownload: ((id: string) => void) | undefined
): React.ReactNode => {
	if (!evalRule(el.rule, data)) {
		return null
	}

	const elAny = el as unknown as Record<string, unknown>

	if (el.type === 'Control') {
		const scope = elAny.scope as string
		if (!scope) {
			return null
		}
		const options = elAny.options as Record<string, unknown> | undefined
		const path = scopeToPath(scope)
		const fieldSchema = resolveSchemaByScope(rootSchema, scope)
		const value = getAtPath(data, path)
		const schemaTitle = (fieldSchema as { title?: string }).title
		const rawLabel =
			(elAny.label as string | undefined) ??
			schemaTitle ??
			path.split('.').at(-1) ??
			path
		// i18next-ignore-dynamic-key
		const label = translate(rawLabel, undefined) || t(rawLabel) || rawLabel
		const formatted = formatFieldValue(
			value,
			fieldSchema,
			options,
			path,
			translate,
			t,
			data
		)

		if (options?.format === 'attachment') {
			return (
				<InfoKeyValue
					className="key-value-container"
					label={label}
					value={
						formatted === '-'
							? '-'
							: (formatted as readonly (
									| VerificationAttachment
									| DraftVerificationAttachment
								)[])
					}
					action={onDownload}
				/>
			)
		}

		return (
			<InfoKeyValue
				className="key-value-container"
				label={label}
				value={formatted as string | number}
			/>
		)
	}

	if (el.type === 'Group') {
		const isFlat =
			(elAny.options as { flat?: boolean } | undefined)?.flat === true
		const groupLabel = elAny.label as string | undefined
		const groupElements = (elAny.elements as UISchemaElement[]) ?? []
		const children = groupElements.map((child, i) => (
			<Fragment key={i}>
				{renderElement(child, data, rootSchema, translate, t, onDownload)}
			</Fragment>
		))
		if (isFlat) {
			return children
		}
		return (
			<DetailsSubContainer>
				{groupLabel && (
					<DetailsTitle>
						{/* i18next-ignore-dynamic-key */}
						{translate(groupLabel, undefined) || t(groupLabel)}
					</DetailsTitle>
				)}
				{children}
			</DetailsSubContainer>
		)
	}

	if (el.type === 'VerticalLayout') {
		const layoutElements = (elAny.elements as UISchemaElement[]) ?? []
		return (
			<>
				{layoutElements.map((child, i) => (
					<Fragment key={i}>
						{renderElement(child, data, rootSchema, translate, t, onDownload)}
					</Fragment>
				))}
			</>
		)
	}

	if (el.type === 'HorizontalLayout') {
		const layoutElements = (elAny.elements as UISchemaElement[]) ?? []
		return (
			<Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
				{layoutElements.map((child, i) => (
					<Box key={i} sx={{ flex: 1 }}>
						{renderElement(child, data, rootSchema, translate, t, onDownload)}
					</Box>
				))}
			</Box>
		)
	}

	if (el.type === 'FieldArrayBlock') {
		const scope = elAny.scope as string
		const fabElements = (elAny.elements as UISchemaElement[]) ?? []
		const arrayPath = scopeToPath(scope)
		const arraySchema = resolveSchemaByScope(rootSchema, scope)
		const itemSchema = (arraySchema.items as JsonSchema | undefined) ?? {}
		const items =
			(getAtPath(data, arrayPath) as Record<string, unknown>[] | undefined) ??
			[]
		return (
			<Box sx={{ mb: 3 }}>
				{items.map((item, index) => (
					<Box key={index} sx={{ pl: 2 }}>
						{index > 0 && <DetailsSpacer />}
						{fabElements.map((child, i) => (
							<Fragment key={i}>
								{renderElement(
									child,
									item,
									itemSchema,
									translate,
									t,
									onDownload
								)}
							</Fragment>
						))}
					</Box>
				))}
			</Box>
		)
	}

	if (el.type === 'EmissionsBreakdown') {
		const scope = elAny.scope as string
		if (!scope) {
			return null
		}
		const breakdownPath = scopeToPath(scope)
		const breakdown = getAtPath(data, breakdownPath) as
			| Record<string, unknown>
			| undefined
		if (!breakdown) {
			return null
		}

		const BREAKDOWN_STAGES = [
			{
				key: 'materialAcquisitionAndPreprocessing',
				label: 'Material Acquisition and Pre-Processing',
			},
			{ key: 'production', label: 'Production' },
			{ key: 'distributionAndStorage', label: 'Distribution and Storage' },
			{ key: 'productUse', label: 'Product Use' },
			{ key: 'endOfLife', label: 'End of Life' },
		] as const

		const answeredUnit = data.answeredUnit as string | undefined
		const unitLabel = answeredUnit
			? (ANSWERED_UNIT_ADORNMENT[
					answeredUnit as keyof typeof ANSWERED_UNIT_ADORNMENT
				] ?? PLACEHOLDER_ADORNMENT)
			: PLACEHOLDER_ADORNMENT

		const stageNodes = BREAKDOWN_STAGES.flatMap(({ key, label }) => {
			const items =
				(breakdown[key] as Record<string, unknown>[] | undefined) ?? []
			return items
				.filter(item => item.value !== undefined && item.value !== null)
				.map((item, index) => (
					<InfoKeyValue
						key={`${key}-${index}`}
						className="key-value-container"
						label={items.length > 1 ? `${label} ${index + 1}` : label}
						value={`${item.value} ${formatUnit(unitLabel)}`}
					/>
				))
		})

		return stageNodes.length > 0 ? stageNodes : null
	}

	// Divider, FormLabel, Infobox, TemplateSelector, Page — skip
	return null
}

const FormDataViewer = ({
	formDefinition,
	formData,
	onDownload,
}: FormDataViewerProps) => {
	const { formSchema, uiSchema, translations } = formDefinition
	const { i18n, t } = useTranslation()

	const translate: TranslateFn = useCallback(
		(key: string, defaultMessage: string | undefined) =>
			translations[i18n.language]?.[key] ?? defaultMessage ?? '',
		[translations, i18n.language]
	)

	const i18nValue = useMemo(() => ({ translate }), [translate])

	const pageElements = useMemo(
		() => (uiSchema as { elements?: UISchemaElement[] }).elements ?? [],
		[uiSchema]
	)

	// Scopes covered by ANY page — used to distinguish truly custom fields
	// from standard fields that simply live on a different page.
	const allCoveredScopes = useMemo(
		() => collectScopesFromElements(pageElements),
		[pageElements]
	)

	return (
		<I18nContext.Provider value={i18nValue}>
			<Box sx={{ display: 'flex', gap: 2 }}>
				<Box sx={{ flexGrow: 10 }}>
					<DetailsContainer>
						{pageElements.map((page, pageIndex) => {
							if (!evalRule(page.rule, formData)) {
								return null
							}
							// Skip pages containing a ProductInfo element — product-header pages
							// whose content is already displayed elsewhere in the UI.
							// NOTE: this heuristic is specific to the emission survey and may not
							// generalise to other custom surveys. If needed, a dedicated uiSchema
							// page option (e.g. options.hideInViewer: true) could replace this.
							const pageAny = page as unknown as Record<string, unknown>
							const pageElems =
								(pageAny.elements as UISchemaElement[] | undefined) ?? []
							if (
								pageElems.some(
									el =>
										(el as unknown as Record<string, unknown>).type ===
										'ProductInfo'
								)
							) {
								return null
							}
							const sectionLabel = pageAny.label as string | undefined
							const sectionElements =
								(pageAny.elements as UISchemaElement[] | undefined) ?? []

							// Detect custom fields: keys present in formData sections that
							// are not covered by any page's UISchema controls.
							// We also restrict to sections referenced on this page so that
							// fields from other pages don't bleed into this section's output.
							const thisPageScopes = collectScopesFromElements(sectionElements)
							const thisPageSectionKeys = new Set(
								[...thisPageScopes]
									.map(s => s.match(/^#\/properties\/([^/]+)/)?.[1])
									.filter((k): k is string => k !== undefined)
							)
							const customFieldsBySection = Object.entries(formData).flatMap(
								([sectionKey, sectionValue]) => {
									if (!thisPageSectionKeys.has(sectionKey)) {
										return []
									}
									if (
										typeof sectionValue !== 'object' ||
										sectionValue === null ||
										Array.isArray(sectionValue)
									) {
										return []
									}
									const entries = Object.entries(
										sectionValue as Record<string, unknown>
									).filter(([fieldKey]) => {
										const prefix = `#/properties/${sectionKey}/properties/${fieldKey}`
										// Exclude if directly covered or if any child scope is covered
										// (handles nested objects like methodologyStandards).
										return (
											!allCoveredScopes.has(prefix) &&
											![...allCoveredScopes].some(s =>
												s.startsWith(prefix + '/')
											)
										)
									})
									if (entries.length === 0) {
										return []
									}
									return entries
								}
							)

							return (
								<DetailsSubContainer key={pageIndex}>
									{sectionLabel && (
										<Typography
											variant="h6"
											sx={{
												pt: 3,
												fontWeight: 600,
												borderBottom: 1,
												borderColor: 'divider',
												pb: 1,
											}}
										>
											{/* i18next-ignore-dynamic-key */}
											{translate(sectionLabel, undefined) || t(sectionLabel)}
										</Typography>
									)}
									{sectionElements.map((el, i) => (
										<Fragment key={i}>
											{renderElement(
												el,
												formData,
												formSchema as JsonSchema,
												translate,
												t,
												onDownload
											)}
										</Fragment>
									))}
									{customFieldsBySection.map(([fieldKey, value]) => (
										<CustomFieldEntry
											key={`${pageIndex}.${fieldKey as string}`}
											fieldKey={fieldKey as string}
											value={value}
										/>
									))}
								</DetailsSubContainer>
							)
						})}
					</DetailsContainer>
				</Box>
			</Box>
		</I18nContext.Provider>
	)
}

export { FormDataViewer }
